
import { defineComponent, PropType } from "vue";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { deleteContentMedia, listContentMedia } from "@/api/ghlContent.api.js";

export default defineComponent({
  name: "content-media-library",
  props: {
    selectedIds: {
      type: Array as PropType<number[]>,
      default: () => []
    },
    imagesOnly: {
      type: Boolean,
      default: false
    },
    maxSelection: {
      type: Number,
      default: 0
    },
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:selectedIds", "refresh"],
  data() {
    return {
      loading: true,
      files: [] as any[],
      usedBytes: 0,
      limitBytes: 0
    };
  },
  computed: {
    storagePercent(): number {
      if (!this.limitBytes) return 0;
      return Math.min(100, Math.round((this.usedBytes / this.limitBytes) * 100));
    },
    visibleFiles(): any[] {
      if (!this.imagesOnly) {
        return this.files;
      }
      return this.files.filter((file) => this.isImage(file));
    }
  },
  async mounted() {
    await this.loadMedia();
  },
  methods: {
    async loadMedia() {
      this.loading = true;
      try {
        const response = await listContentMedia({ limit: 50, offset: 0 });
        const data = response.data || {};
        this.files = data.files || [];
        this.usedBytes = data.usedBytes || 0;
        this.limitBytes = data.limitBytes || 0;
      } catch {
        this.files = [];
      } finally {
        this.loading = false;
      }
    },
    getPublicUrlForId(id: number) {
      const file = this.files.find((entry) => entry.id === id);
      return file?.publicUrl || "";
    },
    formatBytes(bytes: number) {
      if (!bytes) return "0 B";
      const units = ["B", "KB", "MB", "GB"];
      let value = bytes;
      let unit = 0;
      while (value >= 1024 && unit < units.length - 1) {
        value /= 1024;
        unit += 1;
      }
      return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
    },
    isImage(file: any) {
      const mime = String(file.mimeType || "");
      return mime.startsWith("image/") && file.publicUrl;
    },
    statusClass(status: string) {
      if (status === "ready") return "badge-light-success";
      if (status === "pending") return "badge-light-warning";
      if (status === "failed") return "badge-light-danger";
      return "badge-light";
    },
    canAttach(file: any) {
      return file.status === "ready" && !!file.publicUrl;
    },
    toggleAttach(file: any) {
      if (this.readOnly || !this.canAttach(file)) return;
      const ids = [...this.selectedIds];
      const index = ids.indexOf(file.id);
      if (index >= 0) {
        ids.splice(index, 1);
      } else {
        if (this.maxSelection > 0 && ids.length >= this.maxSelection) {
          return;
        }
        ids.push(file.id);
      }
      this.$emit("update:selectedIds", ids);
    },
    async confirmDelete(file: any) {
      const result = await Swal.fire({
        title: "Delete file?",
        text: `Remove "${file.originalFilename}" from storage?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Delete",
        cancelButtonText: "Cancel",
        confirmButtonClass: "btn btn-danger",
        cancelButtonClass: "btn btn-light"
      });
      if (!result.isConfirmed) return;

      try {
        await deleteContentMedia(file.id);
        const ids = this.selectedIds.filter((id) => id !== file.id);
        this.$emit("update:selectedIds", ids);
        await this.loadMedia();
        this.$emit("refresh");
        Swal.fire({
          title: "",
          text: "File deleted.",
          icon: "success",
          confirmButtonClass: "btn btn-secondary"
        });
      } catch (e) {
        Swal.fire({
          title: "",
          text: e?.response?.data?.message || "Unable to delete file.",
          icon: "error",
          confirmButtonClass: "btn btn-secondary"
        });
      }
    }
  }
});
